/**
 * Epic AB#1824 / AB#1831 — Read-only "Effective hours" panel on the Branch
 * card (`/my-business/branches`).
 *
 * The branch card no longer renders an inline hours editor — that surface
 * collapsed into the matrix on `/my-business/business-details > Hours`. The
 * card now shows the 7 day rows as static text plus an "Edit in Business
 * Hours →" link (Dina §I). Divergent days show the per-branch value with a
 * "differs from default ({defaultValue})" hint so the owner can compare at
 * a glance.
 *
 * Used inside `BranchesSection` per the implementation order in the
 * orchestrator prompt.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { interpolate, pluralize } from './interpolate';
import {
    DAY_LONG_KEY,
    WEEK_DAYS_ORDER,
    cellValuesEqual,
    formatCellTime,
    type CellValue,
} from './matrixTypes';
import type {
    EffectiveBranchHoursEntryDto,
    EffectiveBranchHourDto,
} from '../../types/api-responses';
import styles from './BusinessHoursMatrix.module.css';

export interface EffectiveHoursReadOnlyProps {
    /** Per-branch row from `/api/business/hours/effective`. */
    branchEntry: EffectiveBranchHoursEntryDto;
    /**
     * Default-row reference values — used to render the "X differs" hint
     * and the panel sub-header. Empty array = no Default reference available
     * (single-branch or all-divergent case), in which case we treat every
     * day as "matches default" implicitly.
     */
    defaultRow: EffectiveBranchHourDto[];
    /**
     * Optional anchor id passed by the BranchesSection so the "Edit in
     * Business Hours →" link can deep-link to the matrix row. Defaults to
     * `business-hours-matrix-branch-{id}` to match the matrix's row id.
     */
    matrixAnchorId?: string;
}

function dayHoursValue(h: EffectiveBranchHourDto): CellValue {
    return {
        isOpen: h.isOpen,
        openTime: h.isOpen ? h.openTime : null,
        closeTime: h.isOpen ? h.closeTime : null,
        is24Hours: h.is24Hours,
    };
}

const EffectiveHoursReadOnly: React.FC<EffectiveHoursReadOnlyProps> = ({
    branchEntry,
    defaultRow,
    matrixAnchorId,
}) => {
    const { t } = useLanguage();
    const navigate = useNavigate();

    // Build a 7-day map for the branch + default row to keep the render loop
    // simple.
    const branchMap = new Map(
        branchEntry.hours.map((h) => [h.dayOfWeek, dayHoursValue(h)]),
    );
    const defaultMap = new Map(
        defaultRow.map((h) => [h.dayOfWeek, dayHoursValue(h)]),
    );

    let divergeCount = 0;
    WEEK_DAYS_ORDER.forEach((dow) => {
        const b = branchMap.get(dow);
        const d = defaultMap.get(dow);
        if (b && d && !cellValuesEqual(b, d)) divergeCount += 1;
    });

    const headerNote = divergeCount === 0
        ? t('hoursMatrix.branchCard.readonlyHeader.matchesDefault')
        : pluralize(t, 'hoursMatrix.branchCard.readonlyHeader.differs', divergeCount, {
            n: divergeCount,
        });

    const anchorId =
        matrixAnchorId ?? `business-hours-matrix-branch-${branchEntry.branchId}`;

    const handleEditClick = (e: React.MouseEvent) => {
        e.preventDefault();
        // SPA navigation with hash + lightweight scroll hint. The matrix
        // sets its own scroll target via the hash on mount.
        navigate(
            `/my-business/business-details?tab=hours#${anchorId}`,
            { state: { scrollTo: anchorId } },
        );
    };

    return (
        <div
            role="table"
            aria-label={interpolate(t('hoursMatrix.a11y.readonlyTable'), {
                branch: branchEntry.branchName,
            })}
        >
            <div className={styles.readonlyHeader}>
                {t('hoursMatrix.branchCard.readonlyHeader')}{' '}
                <span
                    className={`${styles.readonlyHeaderNote} ${
                        divergeCount > 0 ? styles.readonlyHeaderNoteDiff : ''
                    }`}
                >
                    {headerNote}
                </span>
            </div>

            <div className={styles.effectiveTable}>
                {WEEK_DAYS_ORDER.map((dow) => {
                    const b = branchMap.get(dow);
                    const d = defaultMap.get(dow);
                    const value = b ?? { isOpen: false, openTime: null, closeTime: null, is24Hours: false };
                    const fmt = formatCellTime(value);
                    const diverges = b && d && !cellValuesEqual(b, d);
                    const defaultFmt = d ? formatCellTime(d) : null;
                    const dayLabel = t(DAY_LONG_KEY(dow));
                    return (
                        <React.Fragment key={dow}>
                            <div className={styles.effectiveDay} role="rowheader">
                                {dayLabel}
                            </div>
                            <div
                                className={`${styles.effectiveHrs} ${
                                    fmt.kind === 'closed' ? styles.effectiveHrsClosed : ''
                                } ${diverges ? styles.effectiveHrsDiverges : ''}`}
                                role="cell"
                            >
                                {fmt.kind === 'open' ? fmt.text : t('hoursMatrix.cell.closed')}
                                {diverges && defaultFmt && (
                                    <span className={styles.divergesNote}>
                                        {interpolate(t('hoursMatrix.branchCard.divergesNote'), {
                                            defaultValue:
                                                defaultFmt.kind === 'open'
                                                    ? defaultFmt.text
                                                    : t('hoursMatrix.cell.closed'),
                                        })}
                                    </span>
                                )}
                            </div>
                        </React.Fragment>
                    );
                })}
            </div>

            <button
                type="button"
                className={styles.editLink}
                onClick={handleEditClick}
            >
                {t('hoursMatrix.branchCard.editLink')}
            </button>

            <div className={styles.explainer}>
                {t('hoursMatrix.branchCard.explainer')}
            </div>
        </div>
    );
};

export default EffectiveHoursReadOnly;
